import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FiCheck, FiChevronDown, FiChevronUp, FiX } from 'react-icons/fi';
import { track } from '@/lib/analytics';
import {
  useActivationChecklist,
  useDismissActivation,
  type ActivationItemId,
} from '@/hooks/useActivationChecklist';

const ITEM_ORDER: ActivationItemId[] = [
  'child',
  'custody',
  'notifications',
  'expense',
  'journal',
  'coparent',
];

const ITEM_CTA: Record<ActivationItemId, string> = {
  child: 'Add',
  custody: 'Set up',
  notifications: 'Enable',
  expense: 'Add',
  journal: 'Add',
  coparent: 'Invite',
};

type ActivationChecklistCardProps = {
  onItemAction?: (id: ActivationItemId) => boolean | void;
};

export function ActivationChecklistCard({ onItemAction }: ActivationChecklistCardProps) {
  const navigate = useNavigate();
  const checklist = useActivationChecklist();
  const dismiss = useDismissActivation();
  const [expanded, setExpanded] = useState(true);

  const data = checklist.data;

  const visible = Boolean(
    data && !data.dismissed_at && data.completed_count < data.total_count
  );

  useEffect(() => {
    if (!visible) return;
    track('Activation Checklist Viewed', {
      completed_count: data?.completed_count ?? 0,
      total_count: data?.total_count ?? 6,
    });
  }, [visible, data?.completed_count, data?.total_count]);

  const items = useMemo(() => {
    if (!data) return [];
    const byId = new Map(data.items.map((i) => [i.id, i]));
    return ITEM_ORDER.map((id) => byId.get(id)).filter(Boolean) as {
      id: ActivationItemId;
      completed: boolean;
    }[];
  }, [data]);

  const handleItemClick = useCallback(
    (id: ActivationItemId) => {
      track('Activation Checklist Item Clicked', { item_id: id });
      if (onItemAction?.(id)) return;

      switch (id) {
        case 'child':
          navigate({ to: '/app/settings' });
          break;
        case 'custody':
          navigate({ to: '/app/calendar/add-schedule' });
          break;
        case 'notifications':
          navigate({ to: '/app/notifications' });
          break;
        case 'expense':
          navigate({ to: '/app/expenses/record' });
          break;
        case 'journal':
          navigate({ to: '/app/journal/record', search: { entryType: 'GENERAL' } });
          break;
        case 'coparent':
          navigate({ to: '/app/settings' });
          break;
      }
    },
    [navigate, onItemAction]
  );

  if (!visible || !data) return null;

  const pct = Math.round((data.completed_count / data.total_count) * 100);

  return (
    <div className='bg-white rounded-2xl border border-primary/20 shadow-sm overflow-hidden'>
      <button
        type='button'
        onClick={() => setExpanded((v) => !v)}
        className='w-full flex items-center justify-between px-4 py-3 text-left hover:bg-primary/5 transition-colors'
      >
        <div>
          <p className='font-bold text-gray-900 text-[15px]'>Get started with Harbor</p>
          <p className='text-xs text-gray-500 mt-0.5'>
            {data.completed_count} of {data.total_count} complete
          </p>
        </div>
        {expanded ? (
          <FiChevronUp className='w-5 h-5 text-gray-400 shrink-0' />
        ) : (
          <FiChevronDown className='w-5 h-5 text-gray-400 shrink-0' />
        )}
      </button>

      <div className='h-1 bg-gray-100'>
        <div className='h-full bg-primary transition-all' style={{ width: `${pct}%` }} />
      </div>

      {expanded && (
        <div className='px-4 pb-3 pt-1 space-y-1'>
          {items.map((item) => (
            <div
              key={item.id}
              className='flex items-center justify-between gap-3 py-2 border-b border-gray-50 last:border-0'
            >
              <div className='flex items-center gap-2.5 min-w-0'>
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                    item.completed ? 'bg-primary' : 'border-2 border-gray-300'
                  }`}
                >
                  {item.completed ? <FiCheck className='w-3 h-3 text-white' /> : null}
                </div>
                <span
                  className={`text-sm truncate ${
                    item.completed ? 'text-gray-400 line-through' : 'text-gray-800'
                  }`}
                >
                  {ITEM_ORDER.includes(item.id)
                    ? {
                        child: 'Add your child',
                        custody: 'Set up your custody schedule',
                        notifications: 'Turn on notifications',
                        expense: 'Log your first expense',
                        journal: 'Write your first journal entry',
                        coparent: 'Invite your co-parent',
                      }[item.id]
                    : item.id}
                </span>
              </div>
              {!item.completed && (
                <button
                  type='button'
                  onClick={() => handleItemClick(item.id)}
                  className='text-sm font-semibold text-primary shrink-0 hover:underline'
                >
                  {ITEM_CTA[item.id]}
                </button>
              )}
            </div>
          ))}

          <button
            type='button'
            onClick={() => {
              track('Activation Checklist Dismissed');
              dismiss.mutate();
            }}
            disabled={dismiss.isPending}
            className='mt-2 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600'
          >
            <FiX className='w-3.5 h-3.5' />
            Dismiss checklist
          </button>
        </div>
      )}
    </div>
  );
}
